import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Header } from './components/Header';
import { Navigation } from './components/Navigation';
import { PasswordSetup } from './components/PasswordSetup';
import {
  UserService,
  DataService,
  supabase,
  AUTH_BLOCKED_MESSAGE_KEY,
  DEACTIVATED_ACCOUNT_MESSAGE,
  PRESERVE_PASSWORD_GATE_KEY,
} from './services/supabase';
import { Transaction } from './types';
import { SavedAnalysis, Dashboard } from './types/analytics';
import { setCurrentUserEmail } from './services/preferencesService';
import LoadingSpinner from './components/LoadingSpinner';
import { ThemeProvider } from './contexts/ThemeContext';
import { setUserContext, clearUserContext } from './services/errorTracking';
import Landing from './pages/Landing';
import AuthPage from './pages/Auth';
import AuthCallback from './pages/AuthCallback';
import VerifyEmail from './pages/VerifyEmail';
import Index from './pages/Index';
import EditInventoryCard from './pages/EditInventoryCard';
import Grading from './pages/Grading';
import Expenses from './pages/Expenses';
import TaxCenter from './pages/TaxCenter';
import ReportingLayout from './pages/ReportingLayout';
import AnalysisBuilderPage from './pages/AnalysisBuilderPage';
import Settings from './pages/Settings';
import Subscription from './pages/Subscription';
import SubscribeRequired from './pages/SubscribeRequired';
import NotFound from './pages/NotFound';
import { FeedbackWidget } from './components/FeedbackWidget';
import { Footer } from './components/Footer';
import { StripeService } from './services/stripeService';

const TRIAL_ACCESS_CHECK_TIMEOUT_MS = 12000;
const DATA_LOAD_TIMEOUT_MS = 12000;
const USER_SCHEMA_INIT_TIMEOUT_MS = 12000;
const APP_SPINNER_DEADLOCK_TIMEOUT_MS = 15000;
const ACTIVE_USER_CHECK_TIMEOUT_MS = 5000;

async function withTimeout<T>(promiseLike: PromiseLike<T>, timeoutMs: number, operation: string): Promise<T> {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  try {
    return await Promise.race<T>([
      Promise.resolve(promiseLike),
      new Promise<T>((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error(`${operation} timed out`));
        }, timeoutMs);
      }),
    ]);
  } finally {
    if (timeoutId) clearTimeout(timeoutId);
  }
}

function App() {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [gradingOrders, setGradingOrders] = useState<any[]>([]);
  const [expenses, setExpenses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [authHasSession, setAuthHasSession] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [dataService, setDataService] = useState<DataService | null>(null);
  const loadingRef = useRef(false);
  const [savedAnalyses, setSavedAnalyses] = useState<SavedAnalysis[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [needsPasswordSetup, setNeedsPasswordSetup] = useState(false);
  const [trialBanner, setTrialBanner] = useState<{ daysRemaining: number; endsAt: string } | null>(null);
  /** null = checking, true = show SubscribeRequired only, false = allow app access */
  const [trialExpiredBlockAccess, setTrialExpiredBlockAccess] = useState<boolean | null>(null);

  const isEmailConfirmed = useCallback((currentUser: any): boolean => {
    return Boolean(currentUser?.email_confirmed_at || currentUser?.confirmed_at);
  }, []);

  const signOutUnconfirmedUser = useCallback(async (currentUser: any) => {
    const userService = new UserService();
    setAuthHasSession(false);
    setUser(null);
    setDataService(null);
    setNeedsPasswordSetup(false);
    setTrialExpiredBlockAccess(null);
    setCurrentUserEmail(null);
    sessionStorage.removeItem('supabase_invite_detected');
    await userService.signOut();

    if (typeof window !== 'undefined') {
      const query = currentUser?.email
        ? `?reason=unconfirmed&email=${encodeURIComponent(currentUser.email)}`
        : '?reason=unconfirmed';
      window.location.replace(`/verify-email${query}`);
    }
  }, []);

  const isAuthCallbackPath = useCallback(() => {
    if (typeof window === 'undefined') return false;
    return window.location.pathname === '/auth/callback';
  }, []);

  const redirectToVerifiedLogin = useCallback((email?: string | null) => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams();
    params.set('action', 'login');
    params.set('emailVerified', '1');
    if (email) params.set('email', email);
    window.location.replace(`/auth?${params.toString()}`);
  }, []);

  // Check URL for set-password parameter (from modified invite email template)
  useEffect(() => {
    const checkUrlForPasswordSetup = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const pathname = window.location.pathname;
      
      // Check for set-password indicator in URL (query param or path)
      const hasSetPasswordParam = urlParams.has('set-password') || 
                                  hashParams.has('set-password') ||
                                  pathname.includes('/set-password') ||
                                  pathname.includes('/invite');
      
      if (hasSetPasswordParam) {
        // Store flag in sessionStorage to persist
        sessionStorage.setItem('supabase_invite_detected', 'true');
        console.log('Password setup detected in URL:', { 
          search: window.location.search, 
          hash: window.location.hash,
          pathname 
        });
      }
    };

    // Check immediately
    checkUrlForPasswordSetup();
    
    // Also check after a short delay in case URL changes
    const timeoutId = setTimeout(checkUrlForPasswordSetup, 500);
    
    return () => clearTimeout(timeoutId);
  }, []);

  // Check if user needs to set password (invited user)
  const checkIfNeedsPasswordSetup = useCallback(async (currentUser: any, session: any): Promise<boolean> => {
    if (!currentUser) return false;

    // If user already has password_set flag, they don't need to set it again
    if (currentUser.user_metadata?.password_set === true) {
      sessionStorage.removeItem('supabase_invite_detected');
      return false;
    }

    // Check sessionStorage for invite flag (set from URL check with set-password param)
    const inviteDetected = sessionStorage.getItem('supabase_invite_detected') === 'true';
    
    // Check URL for set-password parameter (from modified invite email)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const pathname = window.location.pathname;
    const hasSetPasswordParam = urlParams.has('set-password') || 
                                hashParams.has('set-password') ||
                                pathname.includes('/set-password') ||
                                pathname.includes('/invite');
    
    // Check user metadata for invite flag
    const wasInvited = currentUser.app_metadata?.invited === true || 
                      currentUser.user_metadata?.invited === true ||
                      inviteDetected ||
                      hasSetPasswordParam;

    if (wasInvited) {
      console.log('Invited user detected:', {
        inviteDetected,
        hasSetPasswordParam,
        app_metadata: currentUser.app_metadata,
        user_metadata: currentUser.user_metadata,
        url: window.location.href
      });
    }

    return wasInvited;
  }, []);

  // Track if we've already initialized to prevent re-initialization on token refresh
  const initializedRef = useRef<Set<string>>(new Set());
  const isInitializingRef = useRef<Set<string>>(new Set());
  const userRef = useRef<any>(null);
  const dataServiceRef = useRef<DataService | null>(null);

  // Update refs when state changes
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    dataServiceRef.current = dataService;
  }, [dataService]);

  // Listen for auth state changes (catches invitation link clicks)
  // WORKAROUND: Do not await any Supabase/client calls inside this callback. Supabase has a deadlock
  // (gotrue-js#762) where async work in onAuthStateChange causes getSession() elsewhere to hang.
  // We defer SIGNED_IN handling with setTimeout(0) so the callback returns immediately.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state change:', event, session?.user?.email);

      // Ignore token refresh events - they shouldn't trigger re-initialization
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed, no action needed');
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        const currentUser = session.user;
        const currentSession = session;
        // Defer all async Supabase work so this callback returns immediately (avoids getSession deadlock).
        setTimeout(() => {
          void (async () => {
            if (isAuthCallbackPath()) {
              const userService = new UserService();
              const verifiedEmail = currentUser.email ?? null;
              await userService.signOut();
              redirectToVerifiedLogin(verifiedEmail);
              return;
            }

            setAuthHasSession(true);
            setAuthChecked(true);
            if (!isEmailConfirmed(currentUser)) {
              await signOutUnconfirmedUser(currentUser);
              return;
            }
            const userEmail = currentUser.email;

            if (userEmail) {
              const userService = new UserService();
              let isActive = true;
              try {
                isActive = await withTimeout(
                  userService.isUserActive(userEmail),
                  ACTIVE_USER_CHECK_TIMEOUT_MS,
                  'Active user check'
                );
              } catch (error) {
                console.warn('Active user check timed out during SIGNED_IN event, allowing session:', error);
                isActive = true;
              }
              if (!isActive) {
                sessionStorage.setItem(AUTH_BLOCKED_MESSAGE_KEY, DEACTIVATED_ACCOUNT_MESSAGE);
                sessionStorage.setItem(PRESERVE_PASSWORD_GATE_KEY, 'true');
                setAuthHasSession(false);
                setUser(null);
                setDataService(null);
                setNeedsPasswordSetup(false);
                setCurrentUserEmail(null);
                await userService.signOut();
                return;
              }

              const isAlreadyInitialized = initializedRef.current.has(userEmail);
              const isCurrentlyInitializing = isInitializingRef.current.has(userEmail);

              if (isAlreadyInitialized && userRef.current?.email === userEmail && !!dataServiceRef.current) {
                console.log('✅ User already initialized, skipping re-initialization');
                return;
              }

              if (isCurrentlyInitializing) {
                if (userRef.current?.email === userEmail && !!dataServiceRef.current) {
                  console.log('⏳ User initialization in progress, skipping duplicate initialization');
                  return;
                }
                isInitializingRef.current.delete(userEmail);
              }

              isInitializingRef.current.add(userEmail);
              console.log('🚀 Starting initialization for:', userEmail);

              const dataService = new DataService(userEmail);
              setUser(currentUser);
              setDataService(dataService);
              setCurrentUserEmail(userEmail);
              setUserContext({ id: currentUser.id, email: userEmail });
              initializedRef.current.add(userEmail);
              isInitializingRef.current.delete(userEmail);

              void Promise.race([
                dataService.ensureUserSchemaExists(),
                new Promise((_, reject) =>
                  setTimeout(() => reject(new Error('Schema initialization timed out')), USER_SCHEMA_INIT_TIMEOUT_MS)
                ),
              ])
                .then(() => {
                  console.log('✅ User schema ensured successfully');
                })
                .catch((error) => {
                  console.error('❌ Error ensuring user schema on sign-in:', error);
                });

              void checkIfNeedsPasswordSetup(currentUser, currentSession)
                .then((needsSetup) => {
                  if (needsSetup) {
                    console.log('Setting needsPasswordSetup to true');
                    setNeedsPasswordSetup(true);
                  }
                })
                .catch((error) => {
                  console.error('Failed password-setup check:', error);
                });
            }
          })();
        }, 0);
        return;
      }

      if (event === 'SIGNED_OUT') {
        setAuthHasSession(false);
        setAuthChecked(true);
        setTrialExpiredBlockAccess(null);
        // Clear initialization tracking
        if (userRef.current?.email) {
          initializedRef.current.delete(userRef.current.email);
          isInitializingRef.current.delete(userRef.current.email);
        }
        
        // Note: No RevenueCat SDK logout needed for Stripe integration
        
        const preservePasswordGate = sessionStorage.getItem(PRESERVE_PASSWORD_GATE_KEY) === 'true';
        if (preservePasswordGate) {
          sessionStorage.removeItem(PRESERVE_PASSWORD_GATE_KEY);
        } else {
          // Clear password gate session for normal sign-outs
          sessionStorage.removeItem('password_gate_passed');
        }
        
        userRef.current = null;
        dataServiceRef.current = null;
        setUser(null);
        setDataService(null);
        setNeedsPasswordSetup(false);
        setCurrentUserEmail(null);
        sessionStorage.removeItem('supabase_invite_detected');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
    // Only depend on checkIfNeedsPasswordSetup, use refs for user/dataService to avoid re-subscribing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkIfNeedsPasswordSetup]);

  // Check for existing authentication (only on mount, not on every render)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const userService = new UserService();
        const { data: { session } } = await withTimeout(
          supabase.auth.getSession(),
          ACTIVE_USER_CHECK_TIMEOUT_MS,
          'Initial session check'
        );
        const sessionUser = session?.user || null;

        if (isAuthCallbackPath() && sessionUser) {
          await userService.signOut();
          redirectToVerifiedLogin(sessionUser.email);
          return;
        }

        setAuthHasSession(!!sessionUser);

        // Fast-path for signed-out users: avoid additional auth/network checks.
        if (!sessionUser) {
          setUser(null);
          setDataService(null);
          setNeedsPasswordSetup(false);
          setTrialExpiredBlockAccess(null);
          setCurrentUserEmail(null);
          return;
        }

        // Use session user directly to avoid an extra network-dependent getUser call here.
        const currentUser = sessionUser;
        if (currentUser && currentUser.email) {
          if (!isEmailConfirmed(currentUser)) {
            await signOutUnconfirmedUser(currentUser);
            return;
          }
          let isActive = true;
          try {
            isActive = await withTimeout(
              userService.isUserActive(currentUser.email),
              ACTIVE_USER_CHECK_TIMEOUT_MS,
              'Active user check'
            );
          } catch (error) {
            console.warn('Active user check timed out during initial auth check, allowing session:', error);
            isActive = true;
          }
          if (!isActive) {
            sessionStorage.setItem(AUTH_BLOCKED_MESSAGE_KEY, DEACTIVATED_ACCOUNT_MESSAGE);
            sessionStorage.setItem(PRESERVE_PASSWORD_GATE_KEY, 'true');
            setAuthHasSession(false);
            setUser(null);
            setDataService(null);
            setNeedsPasswordSetup(false);
            setCurrentUserEmail(null);
            await userService.signOut();
            return;
          }

          // Skip if already initialized
          if (
            initializedRef.current.has(currentUser.email) &&
            userRef.current?.email === currentUser.email &&
            !!dataServiceRef.current
          ) {
            console.log('✅ User already initialized on mount check, skipping');
            return;
          }
          
          // Skip if currently initializing
          if (isInitializingRef.current.has(currentUser.email)) {
            if (userRef.current?.email === currentUser.email && !!dataServiceRef.current) {
              console.log('⏳ User initialization in progress on mount check, skipping');
              return;
            }
            console.warn('⚠️ Clearing stale initializing flag on mount check for:', currentUser.email);
            isInitializingRef.current.delete(currentUser.email);
          }
          
          // Mark as initializing
          isInitializingRef.current.add(currentUser.email);

          // Get current session for password-setup check
          const { data: { session } } = await supabase.auth.getSession();

          const dataService = new DataService(currentUser.email);

          // Commit auth state immediately so auth gate can resolve.
          setUser(currentUser);
          setDataService(dataService);
          setCurrentUserEmail(currentUser.email);
          setUserContext({ id: currentUser.id, email: currentUser.email });
          initializedRef.current.add(currentUser.email);
          isInitializingRef.current.delete(currentUser.email);

          // Ensure schema in background with timeout to prevent deadlocks.
          void Promise.race([
            dataService.ensureUserSchemaExists(),
            new Promise((_, reject) =>
              setTimeout(() => reject(new Error('Schema initialization timed out')), USER_SCHEMA_INIT_TIMEOUT_MS)
            ),
          ])
            .then(() => {
              console.log('✅ User schema ensured successfully');
            })
            .catch((error) => {
              console.error('❌ Error ensuring user schema on auth check:', error);
            });

          // Check if user needs to set password (non-blocking)
          void checkIfNeedsPasswordSetup(currentUser, session)
            .then((needsSetup) => {
              if (needsSetup) {
                setNeedsPasswordSetup(true);
              }
            })
            .catch((error) => {
              console.error('Failed password-setup check:', error);
            });
        } else {
          // Critical fallback: if session lookup fails to produce a user, do not hang on loading spinner.
          setAuthHasSession(false);
          setUser(null);
          setDataService(null);
          setNeedsPasswordSetup(false);
          setTrialExpiredBlockAccess(null);
          setCurrentUserEmail(null);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setAuthHasSession(false);
        setUser(null);
        setDataService(null);
        setNeedsPasswordSetup(false);
        setTrialExpiredBlockAccess(null);
        setCurrentUserEmail(null);
      } finally {
        setAuthChecked(true);
      }
    };

    checkAuth();
    // Only run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Function to load user data from Supabase
  const loadUserData = useCallback(async () => {
    if (!dataService || loadingRef.current) return;

    console.log('🔄 Loading user data for:', dataService.userEmail);
    setLoading(true);
    loadingRef.current = true;

    try {
      // Note: User schema and tables are ensured on sign-in/auth check
      // We don't call it here to avoid unnecessary calls on every data load
      // (it's idempotent, but we want to minimize database calls)
      
      console.log('📊 Fetching transactions...');
      const transactionsData = await withTimeout(
        dataService.getTransactions(),
        DATA_LOAD_TIMEOUT_MS,
        'Transactions load'
      );
      console.log('📊 Transactions fetched:', transactionsData);

      console.log('📊 Fetching grading orders...');
      const gradingOrdersData = await withTimeout(
        dataService.getGradingOrders(),
        DATA_LOAD_TIMEOUT_MS,
        'Grading orders load'
      );
      console.log('📊 Grading orders fetched:', gradingOrdersData);

      console.log('📊 Fetching expenses...');
      const expensesData = await withTimeout(
        dataService.getExpenses(),
        DATA_LOAD_TIMEOUT_MS,
        'Expenses load'
      );
      console.log('📊 Expenses fetched:', expensesData);

      // Set the core data immediately
      setTransactions(transactionsData);
      setGradingOrders(gradingOrdersData);
      setExpenses(expensesData);

      console.log('✅ Core data loaded successfully');

      // Try to load optional data, but don't fail if it doesn't exist
      try {
        console.log('📊 Fetching saved analyses...');
        const savedAnalysesData = await withTimeout(
          dataService.getSavedAnalyses(),
          DATA_LOAD_TIMEOUT_MS,
          'Saved analyses load'
        );
        console.log('📊 Saved analyses fetched:', savedAnalysesData);
        setSavedAnalyses(savedAnalysesData);
      } catch (err) {
        console.warn('⚠️ Saved analyses not available:', err);
        setSavedAnalyses([]);
      }

      try {
        console.log('📊 Fetching dashboards...');
        let dashboardsData = await withTimeout(
          dataService.getDashboards(),
          DATA_LOAD_TIMEOUT_MS,
          'Dashboards load'
        );
        console.log('📊 Dashboards fetched:', dashboardsData);
        const hasOverviewDefault = dashboardsData.some((d: Dashboard) => d.id === 'overview-default');
        if (!hasOverviewDefault) {
          try {
            const ensureResult = await dataService.ensureDefaultDashboard();
            if (ensureResult.created) {
              dashboardsData = await dataService.getDashboards();
            }
          } catch (ensureErr) {
            console.warn('⚠️ ensureDefaultDashboard:', ensureErr);
          }
        }
        setDashboards(dashboardsData);
      } catch (err) {
        console.warn('⚠️ Dashboards not available:', err);
        setDashboards([]);
      }

      console.log('✅ All data loaded successfully');
      // Summary will be calculated in the components that need it
    } catch (err: any) {
      console.error('❌ Error loading user data:', err);
      // Error is logged to console, components handle their own error states
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [dataService]);

  useEffect(() => {
    if (user && dataService) {
      loadUserData();
    }
  }, [user, dataService, loadUserData]);

  // Safety net: if a data load ever gets stuck, unblock UI instead of spinning forever.
  useEffect(() => {
    if (!loading) return;
    const id = setTimeout(() => {
      console.warn('App loading watchdog triggered; clearing stuck loading state');
      loadingRef.current = false;
      setLoading(false);
    }, DATA_LOAD_TIMEOUT_MS + 2000);
    return () => clearTimeout(id);
  }, [loading]);

  // Handle user sign-in
  const handleSignIn = async (signedInUser: any) => {
    if (!signedInUser?.email) {
      console.error('No email in signed in user');
      return;
    }
    
    const dataService = new DataService(signedInUser.email);
    
    // IMPORTANT: Ensure user schema and tables exist immediately on sign-in
    // This must happen before setting state so the user is ready to use the app
    try {
      console.log('🔧 Ensuring user schema exists on sign-in for:', signedInUser.email);
      await dataService.ensureUserSchemaExists();
      console.log('✅ User schema ensured successfully');
    } catch (error) {
      console.error('❌ Error ensuring user schema on sign-in:', error);
      // Don't block sign-in, but log the error
    }
    
    setUser(signedInUser);
    setDataService(dataService);
    
    // Set user context for error tracking
    setUserContext({
      id: signedInUser.id,
      email: signedInUser.email,
    });
    
    // Get current session
    const { data: { session } } = await supabase.auth.getSession();
    
    // Check if user needs to set password
    const needsSetup = await checkIfNeedsPasswordSetup(signedInUser, session);
    if (needsSetup) {
      setNeedsPasswordSetup(true);
    }
  };

  // Handle password setup completion
  const handlePasswordSet = () => {
    setNeedsPasswordSetup(false);
    // Reload user data after password is set
    if (user && dataService) {
      loadUserData();
    }
  };

  const refreshTrialBanner = useCallback(() => {
    if (!user) {
      setTrialBanner(null);
      return;
    }

    if (user.app_metadata?.is_free === true) {
      setTrialBanner(null);
      return;
    }

    const trialEndsAt = user.user_metadata?.trial_ends_at;
    const hasPaymentMethod = user.user_metadata?.has_payment_method === true;

    if (!trialEndsAt || hasPaymentMethod) {
      setTrialBanner(null);
      return;
    }

    const now = new Date();
    const end = new Date(trialEndsAt);
    const msRemaining = end.getTime() - now.getTime();
    const daysRemaining = Math.ceil(msRemaining / (1000 * 60 * 60 * 24));

    if (daysRemaining <= 0) {
      setTrialBanner(null);
      return;
    }

    setTrialBanner({ daysRemaining, endsAt: end.toISOString() });
  }, [user]);

  useEffect(() => {
    refreshTrialBanner();
  }, [refreshTrialBanner]);

  /** Returns true if user must be blocked on SubscribeRequired (trial expired, no payment, no active subscription). */
  const checkTrialExpiredBlockAccess = useCallback(async (currentUser: any): Promise<boolean> => {
    try {
      if (!currentUser) return false;
      if (currentUser.app_metadata?.is_free === true) return false;
      if (currentUser.user_metadata?.has_payment_method === true) return false;
      const trialEndsAt = currentUser.user_metadata?.trial_ends_at;
      if (!trialEndsAt) return false;
      const end = new Date(trialEndsAt);
      if (end.getTime() > Date.now()) return false; // still on trial

      const stripeService = new StripeService();
      let timedOut = false;
      const hasActive = await Promise.race<boolean>([
        stripeService.hasActiveSubscription(),
        new Promise<boolean>((resolve) => {
          setTimeout(() => {
            timedOut = true;
            // Fail open if the subscription check hangs so app routes don't deadlock on loading.
            resolve(true);
          }, TRIAL_ACCESS_CHECK_TIMEOUT_MS);
        }),
      ]);

      if (timedOut) {
        console.warn('Trial access subscription check timed out');
      }

      return !hasActive;
    } catch (error) {
      console.error('Failed to check trial access block, allowing app access:', error);
      // Fail open so transient network/RPC errors do not lock users behind a spinner.
      return false;
    }
  }, []);

  useEffect(() => {
    if (!user || needsPasswordSetup) {
      setTrialExpiredBlockAccess(null);
      return;
    }
    let cancelled = false;
    setTrialExpiredBlockAccess(null);
    const run = async () => {
      try {
        const block = await checkTrialExpiredBlockAccess(user);
        if (!cancelled) setTrialExpiredBlockAccess(block);
      } catch (error) {
        console.error('Unexpected trial access check failure:', error);
        if (!cancelled) setTrialExpiredBlockAccess(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [user, needsPasswordSetup, checkTrialExpiredBlockAccess]);

  // When showing SubscribeRequired, re-check access periodically (e.g. after return from Stripe)
  useEffect(() => {
    if (trialExpiredBlockAccess !== true || !user) return;
    const interval = setInterval(async () => {
      const { data: { user: freshUser } } = await supabase.auth.getUser();
      if (freshUser) {
        const block = await checkTrialExpiredBlockAccess(freshUser);
        if (!block) {
          setUser(freshUser);
          setTrialExpiredBlockAccess(false);
        }
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [trialExpiredBlockAccess, user, checkTrialExpiredBlockAccess]);

  // Handle user sign-out
  const handleSignOut = async () => {
    setTrialExpiredBlockAccess(null);
    const userService = new UserService();
    await userService.signOut();
    
    // Note: No RevenueCat SDK logout needed for Stripe integration
    
        const preservePasswordGate = sessionStorage.getItem(PRESERVE_PASSWORD_GATE_KEY) === 'true';
        if (preservePasswordGate) {
          sessionStorage.removeItem(PRESERVE_PASSWORD_GATE_KEY);
        } else {
          // Clear password gate session for normal sign-outs
          sessionStorage.removeItem('password_gate_passed');
        }
    
    // Clear user context for error tracking
    clearUserContext();
    
    setUser(null);
    setDataService(null);
    setTransactions([]);
    setGradingOrders([]);
    setExpenses([]);
    setSavedAnalyses([]);
    setDashboards([]);
  };

  const shouldBlockForAuth = !authChecked || (authHasSession && !user);

  useEffect(() => {
    if (!shouldBlockForAuth) return;
    const id = setTimeout(() => {
      console.warn('Auth spinner deadlock guard triggered; resetting auth gate state');
      setAuthChecked(true);
      if (authHasSession && !user) {
        setAuthHasSession(false);
      }
    }, APP_SPINNER_DEADLOCK_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [shouldBlockForAuth, authHasSession, user]);

  useEffect(() => {
    if (!user || needsPasswordSetup || trialExpiredBlockAccess !== null) return;
    const id = setTimeout(() => {
      console.warn('Trial-access spinner deadlock guard triggered; allowing app access');
      setTrialExpiredBlockAccess(false);
    }, APP_SPINNER_DEADLOCK_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [user, needsPasswordSetup, trialExpiredBlockAccess]);

  // Show loading spinner until auth state is resolved
  if (shouldBlockForAuth) {
    return (
      <ThemeProvider>
        <LoadingSpinner />
      </ThemeProvider>
    );
  }

  if (!user) {
    return (
      <ThemeProvider>
        <BrowserRouter>
          <div className="min-h-screen flex flex-col">
            <div className="flex-1">
              <Routes>
                <Route path="/auth" element={<AuthPage onAuthSuccess={handleSignIn} />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="*" element={<Landing />} />
              </Routes>
            </div>
            <Footer />
          </div>
        </BrowserRouter>
      </ThemeProvider>
    );
  }

  // Show password setup screen if user needs to set password
  if (needsPasswordSetup) {
    return (
      <ThemeProvider>
        <div className="min-h-screen flex flex-col">
          <div className="flex-1">
            <PasswordSetup user={user} onPasswordSet={handlePasswordSet} />
          </div>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  // Trial expired, no subscription: block app access until they subscribe
  if (trialExpiredBlockAccess === null) {
    return (
      <ThemeProvider>
        <LoadingSpinner />
      </ThemeProvider>
    );
  }
  if (trialExpiredBlockAccess === true) {
    return (
      <ThemeProvider>
        <div className="min-h-screen flex flex-col">
          <div className="flex-1">
            <SubscribeRequired
              onSignOut={handleSignOut}
              onSubscriptionSuccess={() => {
                // Re-fetch user so metadata/subscription can update; re-check will run via interval or we force it
                supabase.auth.getUser().then(({ data: { user: u } }) => {
                  if (u) setUser(u);
                });
                checkTrialExpiredBlockAccess(user).then((block) => setTrialExpiredBlockAccess(block));
              }}
            />
          </div>
          <Footer />
        </div>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <BrowserRouter>
        <div className="min-h-screen bg-background text-foreground">
          <Header user={user} onSignOut={handleSignOut} />
          {trialBanner && (
            <div className="border-b bg-amber-50/90 text-amber-900">
              <div className="container flex flex-col gap-2 py-3 text-sm md:flex-row md:items-center md:justify-between">
                <span>
                  Don't forget, only {trialBanner.daysRemaining} day{trialBanner.daysRemaining === 1 ? '' : 's'} until your free trial expires! Specify a payment method to keep access.
                </span>
                <button
                  type="button"
                  onClick={() => {
                    window.location.href = '/subscription';
                  }}
                  className="font-semibold text-amber-900 underline underline-offset-4 hover:text-amber-700"
                >
                  Add payment method
                </button>
              </div>
            </div>
          )}
          <Navigation />

          <main className="container py-8 space-y-8">
            <Routes>
              <Route path="/auth" element={<Navigate to="/" replace />} />
              <Route path="/auth/callback" element={<AuthCallback />} />
              <Route path="/verify-email" element={<Navigate to="/" replace />} />
              <Route path="/" element={<Index user={user} dataService={dataService} onTransactionsUpdate={setTransactions} onGradingOrdersUpdate={setGradingOrders} transactions={transactions} gradingOrders={gradingOrders} expenses={expenses} />} />
              <Route path="/inventory/new" element={<EditInventoryCard dataService={dataService} transactions={transactions} onTransactionsUpdate={setTransactions} />} />
              <Route path="/inventory/edit/:transactionId" element={<EditInventoryCard dataService={dataService} transactions={transactions} onTransactionsUpdate={setTransactions} />} />
              <Route path="/grading" element={<Grading dataService={dataService} onTransactionsUpdate={setTransactions} onGradingOrdersUpdate={setGradingOrders} gradingOrders={gradingOrders} transactions={transactions} />} />
              <Route path="/expenses" element={<Expenses dataService={dataService} onExpensesUpdate={setExpenses} expenses={expenses} />} />
              <Route path="/tax-center" element={<TaxCenter dataService={dataService} onDataUpdate={loadUserData} transactions={transactions} expenses={expenses} />} />
              <Route
                path="/reporting"
                element={
                  <ReportingLayout
                    dataService={dataService}
                    onDataUpdate={loadUserData}
                    onAnalysesUpdate={setSavedAnalyses}
                    onDashboardsUpdate={setDashboards}
                    savedAnalyses={savedAnalyses}
                    dashboards={dashboards}
                    transactions={transactions}
                    gradingOrders={gradingOrders}
                    expenses={expenses}
                  />
                }
              >
                <Route path="analysis/new" element={<AnalysisBuilderPage />} />
                <Route path="analysis/edit/:analysisId" element={<AnalysisBuilderPage />} />
              </Route>
              <Route path="/settings" element={<Settings dataService={dataService} onTransactionsUpdate={setTransactions} />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </main>
          
          {/* Feedback Widget - Available on all pages */}
          <FeedbackWidget />
          <Footer />
        </div>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;